import React, { useState } from 'react';
import { CartItem } from '../types';
import { initiateCheckout } from '../utils/stripe';
import { getImageUrl } from '../utils/imageUtils';
import styles from './Cart.module.css';

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (itemId: string, newQuantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onCheckout?: () => void;
}

interface ShippingAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const Cart: React.FC<CartProps> = ({ items, onUpdateQuantity, onRemoveItem, onCheckout }) => {
  const [email, setEmail] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [shippingMethod, setShippingMethod] = useState<'pickup' | 'shipping'>('pickup');
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shippingFee = shippingMethod === 'shipping' ? 9.99 : 0;
  const taxRate = 0.0825; // Texas sales tax rate (8.25%)
  
  // Calculate tax: only for pickup (assumes Texas) or shipping within Texas
  const isTexasShipping = shippingAddress.state.toLowerCase().includes('texas') || 
    shippingAddress.state.toLowerCase() === 'tx';
  const shouldAddTax = shippingMethod === 'pickup' || 
    (shippingMethod === 'shipping' && isTexasShipping);
  // Calculate tax on (subtotal + shipping) for shipping orders, or just subtotal for pickup
  const taxableAmount = shippingMethod === 'shipping' ? subtotal + shippingFee : subtotal;
  const taxAmount = shouldAddTax ? taxableAmount * taxRate : 0;
  
  const estimatedTotal = subtotal + shippingFee + taxAmount;

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCheckout = async () => {
    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');
    setIsCheckingOut(true);

    try {
      // Store email in localStorage for the checkout process
      window.localStorage.setItem('checkoutEmail', email);
      
      // Store shipping and tax information for checkout
      const checkoutData = {
        shippingMethod,
        shippingFee,
        taxAmount,
        shippingAddress: shippingMethod === 'shipping' ? shippingAddress : null,
        estimatedTotal
      };
      
      // Debug: Log what we're storing
      console.log('💾 Storing checkout data:', checkoutData);
      console.log('💾 Shipping fee being stored:', shippingFee);
      console.log('💾 Tax amount being stored:', taxAmount);
      
      window.localStorage.setItem('checkoutData', JSON.stringify(checkoutData));

      await initiateCheckout();
      if (onCheckout) onCheckout();
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Checkout failed. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className={styles.cart}>
      <h2 className={styles.cartTitle}>Shopping Cart</h2>
      {items.length === 0 ? (
        <p className={styles.cartEmpty}>Your cart is empty</p>
      ) : (
        <>
          {items.map((item) => (
            <div
              key={item.id}
              className={styles.cartItem}
            >
              <img
                src={getImageUrl(item.image || item.imageUrl)}
                alt={item.name}
                className={styles.cartItemImage}
              />
              <div className={styles.cartItemContent}>
                <h3 className={styles.cartItemName}>{item.name}</h3>
                <p className={styles.cartItemPrice}>${item.price.toFixed(2)}</p>
                {item.size && (
                  <p className={styles.cartItemSize}>
                    Size: {item.size}
                  </p>
                )}
                <div className={styles.quantityControls}>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                    className={styles.quantityButton}
                  >
                    -
                  </button>
                  <span className={styles.quantityDisplay}>{item.quantity}</span>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    className={styles.quantityButton}
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => onRemoveItem(item.id)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <div className={styles.cartSummary}>
            {/* Shipping Method Selection */}
            <div className={styles.shippingSection}>
              <h3 className={styles.sectionTitle}>Delivery Method</h3>
              <div className={styles.shippingOptions}>
                <label className={styles.shippingOption}>
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="pickup"
                    checked={shippingMethod === 'pickup'}
                    onChange={(e) => setShippingMethod(e.target.value as 'pickup' | 'shipping')}
                  />
                  <span>Local Pickup (Free)</span>
                </label>
                <label className={styles.shippingOption}>
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="shipping"
                    checked={shippingMethod === 'shipping'}
                    onChange={(e) => setShippingMethod(e.target.value as 'pickup' | 'shipping')}
                  />
                  <span>Shipping (+$9.99)</span>
                </label>
              </div>
            </div>

            {/* Shipping Address Fields */}
            {shippingMethod === 'shipping' && (
              <div className={styles.shippingAddressSection}>
                <h3 className={styles.sectionTitle}>Shipping Address</h3>
                <div className={styles.addressFields}>
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={shippingAddress.street}
                    onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                    className={styles.addressInput}
                  />
                  <div className={styles.addressRow}>
                    <input
                      type="text"
                      placeholder="City"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      className={styles.addressInput}
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                      className={styles.addressInput}
                    />
                  </div>
                  <div className={styles.addressRow}>
                    <input
                      type="text"
                      placeholder="ZIP Code"
                      value={shippingAddress.zipCode}
                      onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})}
                      className={styles.addressInput}
                    />
                    <select
                      value={shippingAddress.country}
                      onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                      className={styles.addressInput}
                    >
                      <option value="US">United States</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className={styles.orderSummary}>
              <h3 className={styles.sectionTitle}>Order Summary</h3>
              <div className={styles.summaryLine}>
                <span>Subtotal:</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {shippingMethod === 'shipping' && (
                <div className={styles.summaryLine}>
                  <span>Shipping:</span>
                  <span>${shippingFee.toFixed(2)}</span>
                </div>
              )}
              {shouldAddTax && (
                <div className={styles.summaryLine}>
                  <span>Tax (TX):</span>
                  <span>${taxAmount.toFixed(2)}</span>
                </div>
              )}
              <div className={styles.summaryLine + ' ' + styles.totalLine}>
                <span>Estimated Total:</span>
                <span>${estimatedTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Email Collection */}
            <div className={styles.emailSection}>
              <label
                htmlFor="checkout-email"
                className={styles.emailLabel}
              >
                Email Address *
              </label>
              <input
                id="checkout-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                placeholder="Enter your email address"
                className={`${styles.emailInput} ${emailError ? styles.error : ''}`}
                required
              />
              {emailError && (
                <p className={styles.emailError}>
                  {emailError}
                </p>
              )}
              <p className={styles.emailHelp}>
                We&apos;ll send your order confirmation to this email address.
              </p>
            </div>

            <button
              onClick={handleCheckout}
              disabled={isCheckingOut || !email.trim()}
              className={styles.checkoutButton}
            >
              {isCheckingOut ? 'Processing...' : 'Proceed to Checkout'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
